import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
    Badge,
    Button,
    Card,
    CardBody,
    CardTitle,
    Col,
    Form,
    FormGroup,
    Input,
    Label,
    Row,
} from "reactstrap";
import {
    getAllShippingsAPI,
    updateShippingStatusAPI,
} from "../../../../../api/adminShipping.ts";
import { Icon } from "../../../../../components/Component";
import Content from "../../../../../layout/content/Content";
import Head from "../../../../../layout/head/Head";

const ShippingDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [shipping, setShipping] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shippingStatus, setShippingStatus] = useState("");
  const [shippingDetails, setShippingDetails] = useState({
    trackingNumber: "",
    courierService: "",
    shippedAt: "",
    deliveredAt: "",
  });

  useEffect(() => {
    const fetchShippingDetails = async () => {
      try {
        setLoading(true);
        const response = await getAllShippingsAPI();
        const shippingData = response.data.data.find((item) => item._id === id);
        if (!shippingData) throw new Error("Shipping not found");
        setShipping(shippingData);
        setShippingStatus(shippingData.shippingStatus);
        setShippingDetails({
          trackingNumber: shippingData.trackingNumber || "",
          courierService: shippingData.courierService || "",
          shippedAt: shippingData.shippedAt
            ? new Date(shippingData.shippedAt).toISOString().slice(0, 16)
            : "",
          deliveredAt: shippingData.deliveredAt
            ? new Date(shippingData.deliveredAt).toISOString().slice(0, 16)
            : "",
        });
      } catch (error) {
        console.error("Failed to fetch shipping details:", error);
        toast.error("Failed to load shipping details");
      } finally {
        setLoading(false);
      }
    };

    fetchShippingDetails();
  }, [id]);

  const handleStatusUpdate = async () => {
    try {
      const updateBody = {
        shippingStatus,
        trackingNumber: shippingDetails.trackingNumber,
        courierService: shippingDetails.courierService,
        shippedAt: shippingDetails.shippedAt
          ? new Date(shippingDetails.shippedAt).toISOString()
          : null,
        deliveredAt: shippingDetails.deliveredAt
          ? new Date(shippingDetails.deliveredAt).toISOString()
          : null,
      };
      await updateShippingStatusAPI({
        id,
        body: updateBody,
      });
      setShipping((prev) => ({
        ...prev,
        ...updateBody,
      }));
      toast.success(`Shipping status updated to ${shippingStatus}`);
    } catch (error) {
      console.error("Failed to update shipping status:", error);
      toast.error("Failed to update shipping status");
    }
  };

  const handleDetailChange = (field, value) => {
    setShippingDetails((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const statusOptions = ["Pending", "Shipped", "In Transit", "Delivered", "Cancelled"];

  if (loading) {
    return (
      <React.Fragment>
        <Head title="Shipping Details" />
        <Content>
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </Content>
      </React.Fragment>
    );
  }

  if (!shipping) {
    return (
      <React.Fragment>
        <Head title="Shipping Details" />
        <Content>
          <div className="text-center py-4">
            <p className="text-danger mb-3">Shipping record not found</p>
            <Button color="primary" onClick={() => navigate("/admin/shippings")}>
              Back to Shipping List
            </Button>
          </div>
        </Content>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <Head title={`Shipping Details - ${shipping._id}`} />
      <Content>
        <div className="container-fluid py-4">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h3 className="text-primary">Shipping #{shipping._id}</h3>
            <Button
              color="secondary"
              onClick={() => navigate("/admin/shippings")}
              className="btn-icon"
            >
              <Icon name="arrow-left" /> Back to Shipments
            </Button>
          </div>

          <Row>
            {/* Shipping Info */}
            <Col lg={8}>
              <Card className="shadow-sm border-0 mb-4">
                <CardBody>
                  <CardTitle tag="h5" className="text-dark fw-bold mb-4">
                    Shipping Information
                  </CardTitle>
                  <Row>
                    <Col md={6}>
                      <div className="mb-4">
                        <strong className="text-muted d-block mb-1">Customer:</strong>
                        <p className="mb-1">{`${shipping.userId.first_name} ${shipping.userId.last_name}`}</p>
                        <p className="text-muted">{shipping.userId.email}</p>
                      </div>
                      <div className="mb-4">
                        <strong className="text-muted d-block mb-1">Order Details:</strong>
                        <p className="mb-1">Order ID: {shipping.orderId._id}</p>
                        <p className="mb-1">
                          Order Date: {new Date(shipping.orderId.orderDate).toLocaleString()}
                        </p>
                        <p className="text-dark fw-bold">
                          Total: ₹{shipping.orderId.totalAmount.toFixed(2)}
                        </p>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-4">
                        <strong className="text-muted d-block mb-1">Shipping Address:</strong>
                        <p className="mb-1">{shipping.addressSnapshot.addressLine1}</p>
                        <p className="mb-1">
                          {shipping.addressSnapshot.city}, {shipping.addressSnapshot.state}
                        </p>
                        <p className="mb-1">
                          {shipping.addressSnapshot.postalCode}, {shipping.addressSnapshot.country}
                        </p>
                      </div>
                      <div className="mb-4">
                        <strong className="text-muted d-block mb-1">Courier Service:</strong>
                        <p>{shipping.courierService}</p>
                      </div>
                      <div className="mb-4">
                        <strong className="text-muted d-block mb-1">Estimated Delivery:</strong>
                        <p>
                          {shipping.estimatedDeliveryDate
                            ? new Date(shipping.estimatedDeliveryDate).toLocaleString()
                            : "N/A"}
                        </p>
                      </div>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>

            {/* Status and Actions */}
            <Col lg={4}>
              <Card className="shadow-sm border-0">
                <CardBody>
                  <CardTitle tag="h5" className="text-dark fw-bold mb-4">
                    Shipping Management
                  </CardTitle>
                  <Form>
                    <FormGroup>
                      <Label for="shippingStatus" className="text-muted fw-medium">
                        Status
                      </Label>
                      <Input
                        type="select"
                        name="shippingStatus"
                        id="shippingStatus"
                        value={shippingStatus}
                        onChange={(e) => setShippingStatus(e.target.value)}
                        className="mb-3"
                      >
                        {statusOptions.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </Input>
                    </FormGroup>
                    <FormGroup>
                      <Label for="trackingNumber" className="text-muted fw-medium">
                        Tracking Number
                      </Label>
                      <Input
                        type="text"
                        name="trackingNumber"
                        id="trackingNumber"
                        value={shippingDetails.trackingNumber}
                        onChange={(e) => handleDetailChange("trackingNumber", e.target.value)}
                        className="mb-3"
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label for="courierService" className="text-muted fw-medium">
                        Courier Service
                      </Label>
                      <Input
                        type="text"
                        name="courierService"
                        id="courierService"
                        value={shippingDetails.courierService}
                        onChange={(e) => handleDetailChange("courierService", e.target.value)}
                        className="mb-3"
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label for="shippedAt" className="text-muted fw-medium">
                        Shipped At
                      </Label>
                      <Input
                        type="datetime-local"
                        name="shippedAt"
                        id="shippedAt"
                        value={shippingDetails.shippedAt}
                        onChange={(e) => handleDetailChange("shippedAt", e.target.value)}
                        className="mb-3"
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label for="deliveredAt" className="text-muted fw-medium">
                        Delivered At
                      </Label>
                      <Input
                        type="datetime-local"
                        name="deliveredAt"
                        id="deliveredAt"
                        value={shippingDetails.deliveredAt}
                        onChange={(e) => handleDetailChange("deliveredAt", e.target.value)}
                        className="mb-3"
                      />
                    </FormGroup>
                    <Button
                      color="primary"
                      onClick={handleStatusUpdate}
                      disabled={
                        shippingStatus === shipping.shippingStatus &&
                        shippingDetails.trackingNumber === (shipping.trackingNumber || "") &&
                        shippingDetails.courierService === (shipping.courierService || "") &&
                        shippingDetails.shippedAt ===
                          (shipping.shippedAt
                            ? new Date(shipping.shippedAt).toISOString().slice(0, 16)
                            : "") &&
                        shippingDetails.deliveredAt ===
                          (shipping.deliveredAt
                            ? new Date(shipping.deliveredAt).toISOString().slice(0, 16)
                            : "")
                      }
                      className="w-100"
                    >
                      Update Shipping
                    </Button>
                  </Form>
                  <div className="mt-4">
                    <h6 className="text-muted fw-medium">Current Status:</h6>
                    <Badge
                      color={
                        shipping.shippingStatus === "Delivered"
                          ? "success"
                          : shipping.shippingStatus === "Cancelled"
                          ? "danger"
                          : "warning"
                      }
                      className="px-3 py-2"
                    >
                      {shipping.shippingStatus}
                    </Badge>
                  </div>
                  <div className="mt-3">
                    <h6 className="text-muted fw-medium">Created At:</h6>
                    <p>{new Date(shipping.createdAt).toLocaleString()}</p>
                  </div>
                  <div className="mt-3">
                    <h6 className="text-muted fw-medium">Updated At:</h6>
                    <p>{new Date(shipping.updatedAt).toLocaleString()}</p>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </Content>
    </React.Fragment>
  );
};

export default ShippingDetails;